"use client";

// ЕДИНАЯ кнопка форм и диалогов портала.
//
// TabButton (tab-header.tsx) — это кнопка-пилюля для ШАПКИ вкладки. Внутри форм,
// модалок и панелей нужна прямоугольная кнопка, и до сих пор каждый экран
// собирал её сам: где `glass-hover rounded-[var(--radius-md)] px-3 py-1.5 text-[12px]`, где своя
// константа btnPrimary, где голый <button> с инлайн-стилем.
//
// Варианты:
//   primary   — главное действие (Сохранить, Создать), заливка var(--primary)
//   secondary — нейтральное (Отмена), поверхность + рамка
//   danger    — разрушающее (Удалить), красный сигнальный
//   ghost     — без рамки, для второстепенных действий в плотных местах
//
// ⚠️ boxShadow: "none" обязателен: в светлой теме globals.css вешает тень на
// КАЖДУЮ <button>, кроме .glass-* — без сброса кнопка «пузырится».

import { forwardRef, useCallback, useEffect, useRef, useState } from "react";
import type { ButtonHTMLAttributes, CSSProperties, MouseEvent, ReactNode } from "react";

export type ButtonVariant = "primary" | "secondary" | "danger" | "ghost";

// ── Форма кнопки: прямоугольник, а не пилюля ─────────────────────────────────
// Кнопки форм и диалогов приведены к референсу PLUX (UI-кит от 25.08): там
// прямоугольники со скруглением, пилюль нет вовсе. Радиус берём из темы
// (--radius-md), а не числом: покрутили общий радиус в конструкторе — кнопки
// поехали вместе со всем остальным.
//
// ⚠️ ЭТО НЕ ТРОГАЕТ НАВИГАЦИЮ, и так и задумано. Пилюли вкладок
// (components/ui/tab-header.tsx) и пункты рельса (components/app-shell.tsx)
// держат СВОИ строки классов — раньше я попробовал перебить радиус общим
// правилом по `button.rounded-full` и выпрямил заодно всё меню (правка Максима
// 26.08.2026). Разные файлы здесь — не дублирование, а граница: кнопка внутри
// формы и пункт навигации — разные вещи и меняются по отдельности.
export const BTN_CLASS =
  "glass-hover inline-flex h-[30px] shrink-0 items-center justify-center gap-1.5 rounded-[var(--radius-md)] border px-3 text-[12px] font-semibold transition disabled:opacity-50 disabled:pointer-events-none";

/** Готовые строки классов — для мест, где нужен className, а не компонент
 *  (<label> с input[type=file], ссылки-кнопки, старый код с const btnPrimary). */
export const BTN_PRIMARY_CLASS =
  `${BTN_CLASS} tab-btn-primary border-transparent bg-[var(--primary)] text-[var(--primaryText)] shadow-none`;
export const BTN_SECONDARY_CLASS =
  `${BTN_CLASS} border-[var(--border)] bg-[var(--surface)] text-[var(--text)] shadow-none`;

export function buttonStyle(variant: ButtonVariant = "secondary"): CSSProperties {
  const base: CSSProperties = { boxShadow: "none" };
  switch (variant) {
    case "primary":
      return { ...base, borderColor: "transparent", background: "var(--primary)", color: "var(--primaryText)" };
    case "danger":
      return {
        ...base,
        borderColor: "color-mix(in srgb, var(--danger) 45%, transparent)",
        background: "color-mix(in srgb, var(--danger) 12%, transparent)",
        color: "var(--danger)",
      };
    case "ghost":
      return { ...base, borderColor: "transparent", background: "transparent", color: "var(--text)" };
    default:
      return { ...base, borderColor: "var(--border)", background: "var(--surface)", color: "var(--text)" };
  }
}

type ButtonProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, "title"> & {
  /** Всплывающая подсказка (data-tooltip портала). */
  title?: string;
  variant?: ButtonVariant;
  /** Кнопка только с иконкой: квадрат 30×30 без горизонтальных отступов. */
  iconOnly?: boolean;
  /** Долгое действие (выгрузка, синхронизация, пересчёт). Кнопка сама показывает
   *  ход: кольцо внутри себя, затем на 1,4 с зелёную галочку. Пока идёт —
   *  повторный клик не проходит. Ошибку не глотаем: пробрасываем наверх, кнопка
   *  просто возвращается в покой (сообщение — забота вызывающего, через toast). */
  onAsyncClick?: (e: MouseEvent<HTMLButtonElement>) => Promise<unknown>;
  /** Подпись на время работы. Без неё остаются исходные children. */
  busyLabel?: ReactNode;
  /** Подпись после успеха (по умолчанию «Готово»). */
  doneLabel?: ReactNode;
  /** Отключить заливку-свип у вторичной кнопки — например у «Отмены»,
   *  которой фиолетовая заливка на ховере даёт вес главного действия. */
  noSweep?: boolean;
  children: ReactNode;
};

/** Как долго держим зелёную галочку перед возвратом в покой. */
const DONE_HOLD_MS = 1400;

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = "secondary",
    iconOnly,
    className,
    title,
    children,
    type = "button",
    onAsyncClick,
    busyLabel,
    doneLabel = "Готово",
    noSweep,
    onClick,
    disabled,
    ...rest
  },
  ref,
) {
  const [state, setState] = useState<"idle" | "run" | "done">("idle");
  // Кнопка может исчезнуть вместе с модалкой, пока идёт запрос — setState после
  // размонтирования React 18 не ругает, но таймер держать незачем.
  const aliveRef = useRef(true);
  const holdRef = useRef<number | null>(null);
  useEffect(() => {
    // Флаг взводим НА КАЖДОМ монтировании, а не только в начале: в dev React
    // StrictMode монтирует эффект дважды (mount → cleanup → mount), и без этой
    // строки после первой же чистки aliveRef оставался false — кнопка навсегда
    // залипала в состоянии «идёт», потому что ответ приходил «мёртвой» кнопке.
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
      if (holdRef.current != null) window.clearTimeout(holdRef.current);
    };
  }, []);

  const handleClick = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      if (!onAsyncClick) { onClick?.(e); return; }
      if (state !== "idle") return;
      onClick?.(e);
      setState("run");
      void onAsyncClick(e)
        .then(() => {
          if (!aliveRef.current) return;
          setState("done");
          holdRef.current = window.setTimeout(() => {
            if (aliveRef.current) setState("idle");
          }, DONE_HOLD_MS);
        })
        .catch((err) => {
          if (aliveRef.current) setState("idle");
          // Сообщение об ошибке — забота вызывающего (у него свой toast и текст).
          // Пробросить наверх нельзя: промис уже отвязан (void), и rethrow даст
          // unhandled rejection вместо сообщения. Оставляем след в консоли.
          console.error("Button.onAsyncClick:", err);
        });
    },
    [onAsyncClick, onClick, state],
  );

  // Свип — только у вторичной кнопки: у primary заливка уже фиолетовая,
  // у danger свой красный язык, ghost живёт без рамки и фона.
  // ⚠️ ЗАЛИВКА-СВИП УБРАНА (Максим, 31.08.2026: «убери такого рода анимации»).
  // Вторичная кнопка при наведении больше не заливается фирменным цветом слева
  // направо за 0,42 с — остаётся обычная подсветка фона. Проп `noSweep` оставлен
  // у типа: он расставлен по вызовам, его уборка — отдельная правка.

  return (
    <button
      {...rest}
      ref={ref}
      type={type}
      onClick={handleClick}
      disabled={disabled || state !== "idle"}
      data-state={onAsyncClick ? state : undefined}
      data-tooltip={title}
      // tab-btn-primary — тот же приём, что у TabButton: ховер .glass-hover в
      // светлой теме красит фон в белый, и белый текст primary-кнопки исчезал.
      // glass-btn-danger — фирменное красное свечение на ховере из globals.css.
      // [&>svg]:shrink-0 — SVG в флексе сжимается: у него min-content ширина 0,
      // и значок в квадратной кнопке выходил уже, чем выше (корзина 10×17 при
      // размере 17, Максим 27.08.2026).
      className={`${BTN_CLASS} ${iconOnly ? "w-[30px] px-0 [&>svg]:shrink-0" : ""} ${
        variant === "primary" ? "tab-btn-primary" : ""
      } ${variant === "danger" ? "glass-btn-danger" : ""} ${
        onAsyncClick ? "btn-job" : ""
      } ${className ?? ""}`}
      style={buttonStyle(variant)}
    >
      {state === "run" && <span className="btn-ring" aria-hidden />}
      {state === "done" && (
        <svg className="btn-tick" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
             strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M20 6 9 17l-5-5" />
        </svg>
      )}
      {state === "run" ? (busyLabel ?? children) : state === "done" ? doneLabel : children}
    </button>
  );
});
